// Cable Image Capture Service - Server-Side SVG Generation
// This service sends configuration data to the EC2 server for SVG generation and PNG conversion

import { API_ENDPOINTS } from '../config';

class CableImageCaptureService {
  constructor() {
    // Use the domain endpoint since the EC2 is behind nginx
    this.baseUrl = process.env.REACT_APP_EC2_IMAGE_GENERATOR_URL || 'https://nlfx-cable.luknai.com';
    console.log('🔧 [CABLE-IMAGE-SERVICE] Using domain endpoint:', this.baseUrl);
    console.log('🔧 [CABLE-IMAGE-SERVICE] Environment variable:', process.env.REACT_APP_EC2_IMAGE_GENERATOR_URL);
  }

  /**
   * Generate cable image using server-side SVG generation
   * @param {Object} config - Configuration object containing all cable data
   * @param {string} style - Cable style ('wye', 'zip', 'hybrid', 'hybrid-power-audio-audio')
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async generateCableImage(config, style, options = {}) {
    try {
      console.log(`🔄 [SERVER-SIDE] Generating ${style} cable image with config:`, config);

      const response = await fetch(`${this.baseUrl}/generate-cable-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          style,
          config,
          timestamp: Date.now(),
          options
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ [SERVER-SIDE] ${style} cable image generated successfully`);
        return {
          success: true,
          s3Url: result.s3Url, // Return the S3 URL from the server
          imageData: result.imageData,
          svgData: result.svgData, // Optional: return the generated SVG for debugging
          metadata: result.metadata
        };
      } else {
        console.error(`❌ [SERVER-SIDE] ${style} cable image generation failed:`, result.error);
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error(`❌ [SERVER-SIDE] Error generating ${style} cable image:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Legacy method for backward compatibility - now uses server-side generation
   * @param {HTMLElement} svgElement - SVG element (no longer used)
   * @param {string} style - Cable style
   * @param {Object} config - Configuration object
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async captureCableImage(svgElement, style, config = null) {
    console.warn('⚠️ [DEPRECATED] captureCableImage called - use generateCableImage instead');
    
    // If no config provided, try to extract from the DOM (fallback)
    if (!config) {
      console.warn('⚠️ No config provided, attempting DOM extraction as fallback');
      config = this.extractConfigFromDOM(svgElement, style);
    }
    
    return this.generateCableImage(config, style);
  }

  /**
   * Extract configuration from DOM as fallback (legacy support)
   * @param {HTMLElement} svgElement - SVG element
   * @param {string} style - Cable style
   * @returns {Object} - Configuration object
   */
  extractConfigFromDOM(svgElement, style) {
    // This is a fallback method - in practice, we should always pass config
    console.warn('⚠️ [FALLBACK] Extracting config from DOM - this is unreliable');
    
    // Try to find configuration data in the DOM or global state
    // This is not ideal but provides backward compatibility
    return {
      style,
      lengths: {},
      connectors: {},
      wireType: 'PSM',
      specialInstructions: ''
    };
  }

  /**
   * Generate Wye cable image with specific configuration
   * @param {Object} config - Wye cable configuration
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async generateWyeImage(config) {
    return this.generateCableImage(config, 'wye');
  }

  /**
   * Generate Zip cable image with specific configuration
   * @param {Object} config - Zip cable configuration
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async generateZipImage(config) {
    return this.generateCableImage(config, 'zip');
  }

  /**
   * Generate Hybrid cable image with specific configuration
   * @param {Object} config - Hybrid cable configuration
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async generateHybridImage(config) {
    return this.generateCableImage(config, 'hybrid');
  }

  /**
   * Generate Hybrid Power Audio Audio cable image with specific configuration
   * @param {Object} config - Hybrid Power Audio Audio cable configuration
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async generateHybridPowerAudioAudioImage(config) {
    return this.generateCableImage(config, 'hybrid-power-audio-audio');
  }

  /**
   * Get captured image by SKU (for BigCommerce integration)
   * @param {string} sku - SKU to retrieve image for
   * @returns {Promise<Object>} - Result with success status and image data
   */
  async getCapturedImage(sku) {
    try {
      console.log(`🔄 [CAPTURE-SERVICE] Retrieving captured image for SKU: ${sku}`);
      
      const response = await fetch(`${this.baseUrl}/api/get-captured-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sku,
          timestamp: Date.now()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ [CAPTURE-SERVICE] Retrieved captured image for SKU: ${sku}`);
        return {
          success: true,
          imageData: result.imageData,
          s3Url: result.s3Url,
          metadata: result.metadata
        };
      } else {
        console.warn(`⚠️ [CAPTURE-SERVICE] No captured image found for SKU: ${sku}`);
        return {
          success: false,
          error: result.error || 'Image not found'
        };
      }
    } catch (error) {
      console.error(`❌ [CAPTURE-SERVICE] Error retrieving captured image for SKU ${sku}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Clear image cache (for testing and cleanup)
   */
  clearImageCache() {
    try {
      // Clear localStorage cache
      localStorage.removeItem('nlfx_cable_image_cache');
      localStorage.removeItem('nlfx_cable_image_timestamp');
      console.log('✅ [CAPTURE-SERVICE] Image cache cleared');
    } catch (error) {
      console.error('❌ [CAPTURE-SERVICE] Error clearing image cache:', error);
    }
  }

  /**
   * Check if image exists for given configuration
   * @param {Object} config - Configuration object
   * @returns {boolean} - True if image exists
   */
  hasCapturedImage(config) {
    try {
      const cacheKey = this.generateCacheKey(config);
      const cachedData = localStorage.getItem('nlfx_cable_image_cache');
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        return parsed[cacheKey] !== undefined;
      }
      
      return false;
    } catch (error) {
      console.error('❌ [CAPTURE-SERVICE] Error checking captured image:', error);
      return false;
    }
  }

  /**
   * Convert data URL to base64 string
   * @param {string} dataUrl - Data URL to convert
   * @returns {string|null} - Base64 string or null
   */
  convertToBase64(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string') {
      return null;
    }
    
    try {
      const base64Match = dataUrl.match(/data:image\/[^;]+;base64,(.+)/);
      return base64Match ? base64Match[1] : null;
    } catch (error) {
      console.error('❌ [CAPTURE-SERVICE] Error converting to base64:', error);
      return null;
    }
  }

  /**
   * Generate cache key for configuration
   * @param {Object} config - Configuration object
   * @returns {string} - Cache key
   */
  generateCacheKey(config) {
    return `cable_${config.style || 'unknown'}_${config.type || 'unknown'}_${Date.now()}`;
  }

  /**
   * Test capture functionality
   * @param {HTMLElement} testElement - Test SVG element
   * @returns {Promise<Object>} - Test result
   */
  async testCapture(testElement) {
    try {
      console.log('🧪 [CAPTURE-SERVICE] Testing capture functionality');
      
      const testConfig = {
        style: 'test',
        type: 'test',
        length: 10,
        connectors: {
          endA: { connector: 'test-a' },
          endB: { connector: 'test-b' }
        }
      };
      
      const result = await this.generateCableImage(testConfig, 'test');
      
      console.log('🧪 [CAPTURE-SERVICE] Test capture result:', result);
      return result;
    } catch (error) {
      console.error('❌ [CAPTURE-SERVICE] Test capture failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Test the server connection
   * @returns {Promise<boolean>} - True if server is reachable
   */
  async testServerConnection() {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      return response.ok;
    } catch (error) {
      console.error('❌ Server connection test failed:', error);
      return false;
    }
  }
}

// Create singleton instance
const cableImageCaptureService = new CableImageCaptureService();

export { cableImageCaptureService };
